import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined"
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined"
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined"
import Button from "@mui/material/Button"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../common/components/CloseableSnackbar"
import Column, {
  CenterEverythingColumn,
} from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { formatCurrency, formatDate, useIsMobile } from "../../../common/utils"
import { trimLongString } from "../../../common/utils"
import drawerBg from "../../../static/images/drawer--background-v4.png"
import BorderedGrayChip from "../components/BorderedGrayChip"
import CreateOfferDialog from "../components/offers/CreateOfferDialog"
import OfferDetailsDialog from "../proposals/OfferDetailsDialog"
import {
  OFFER_STATUS_CHOICES,
  addCRMEventToCandidate,
  commissionRatioDisplay,
  removeCRMEventFromCandidate,
  updateCRMEventForCandidate,
  useScrollToTargetElementRef,
} from "../utils"
import ColumnPaper from "./ColumnPaper"
import IconTitleValue from "./IconTitleValue"
import { EVENT_TYPES } from "./data"
import { GLOBAL_SEARCH_URL_PARAMS } from "./data"

export default function OffersColumn({
  candidate,
  setCandidate,
  isLoading = false,
}) {
  const [createOfferDialogOpen, setCreateOfferDialogOpen] = useState(false)
  const {
    open: openSnackbar,
    close: closeSnackbar,
    isOpen: isSnackbarOpen,
    message: snackbarMessage,
  } = useCloseableSnackbarState()
  const axios = useAuthorizedAxiosClient()
  const offers = candidate.crm_events.filter(
    (event) => event.type === EVENT_TYPES.offer
  )
  const sortedOffers = offers.slice()
  sortedOffers.sort(
    (offer1, offer2) => new Date(offer2.created) - new Date(offer1.created)
  )

  function handleCreateOfferDialogClosed(createdOffer) {
    if (createdOffer) {
      addCRMEventToCandidate(setCandidate, createdOffer, EVENT_TYPES.offer)
    }
    setCreateOfferDialogOpen(false)
  }

  function handleOfferDeleted(offer) {
    axios.delete(`/crm/api/offers/${offer.id}/`).then(({ data }) => {
      removeCRMEventFromCandidate(setCandidate, offer, EVENT_TYPES.offer)
      const annualValue = data.annual_value
      setCandidate((candidate) => ({ ...candidate, annual_value: annualValue }))
      let message = "Proposal deleted successfully"
      if (annualValue) {
        message += `. New annual value is ${annualValue.toLocaleString()}.`
      }
      openSnackbar(message)
    })
  }

  function handleOfferUpdated(updatedOffer) {
    updateCRMEventForCandidate(setCandidate, updatedOffer, EVENT_TYPES.offer)
  }

  return (
    <OffersCol>
      <Row justifyContent="space-between" alignItems="center">
        <Typography variant="h4">Current Proposals</Typography>
        <Button
          variant="text"
          color="primary"
          startIcon={<AddOutlinedIcon sx={{ color: "primary.main" }} />}
          onClick={() => setCreateOfferDialogOpen(true)}
          disabled={isLoading}
        >
          New
        </Button>
        <CreateOfferDialog
          open={createOfferDialogOpen}
          onClose={handleCreateOfferDialogClosed}
          candidate={candidate}
        />
      </Row>
      <Column sx={{ mt: 3, gap: 2 }}>
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <OfferSkeleton key={i} />)
        ) : sortedOffers.length === 0 ? (
          <CenterEverythingColumn>
            <Typography variant="body1" color="text.secondary">
              No proposal created yet.
            </Typography>
          </CenterEverythingColumn>
        ) : (
          sortedOffers.map((offer) => (
            <Offer
              key={offer.id}
              offer={offer}
              onOfferDeleted={handleOfferDeleted}
              onOfferUpdated={handleOfferUpdated}
            />
          ))
        )}
      </Column>
      <CloseableSnackbar
        open={isSnackbarOpen}
        onClose={closeSnackbar}
        message={snackbarMessage}
      />
    </OffersCol>
  )
}

function Offer({ offer, onOfferDeleted, onOfferUpdated }) {
  const scrollRef = useScrollToTargetElementRef(
    offer,
    GLOBAL_SEARCH_URL_PARAMS.offer
  )
  const [menuRef, setMenuRef] = useState(null)
  const [offerDetailsDialogOpen, setOfferDetailsDialogOpen] = useState(false)
  const isMobile = useIsMobile()
  const titleFontSize = isMobile ? "11px" : "14px"

  function handleDeleteMenuItemClicked() {
    closeMenu()
    onOfferDeleted(offer)
  }

  function closeMenu() {
    setMenuRef(null)
  }

  function handleOfferLinkClicked(e) {
    e.preventDefault()
    setOfferDetailsDialogOpen(true)
  }

  function handleOfferDetailsDialogClosed(offer) {
    if (offer) {
      onOfferUpdated(offer)
    }
    setOfferDetailsDialogOpen(false)
  }

  return (
    <OfferPaper key={offer.id} ref={scrollRef}>
      <Column>
        <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Link
            variant="h5"
            color="inherit"
            href="#"
            onClick={handleOfferLinkClicked}
            title={offer.template.name}
            lineHeight={1.2}
          >
            {trimLongString(offer.template.name, 25)}
          </Link>
          <Row sx={{ alignItems: "center" }}>
            <OfferStatusChip label={OFFER_STATUS_CHOICES[offer.status]} />
            <IconButton
              size="small"
              onClick={(e) => setMenuRef(e.currentTarget)}
            >
              <MoreVertOutlinedIcon />
            </IconButton>
          </Row>
          <Menu
            anchorEl={menuRef}
            open={!!menuRef}
            onClose={closeMenu}
            anchorOrigin={{
              horizontal: "center",
              vertical: "bottom",
            }}
            transformOrigin={{
              horizontal: "right",
              vertical: "top",
            }}
          >
            <MenuItem onClick={handleDeleteMenuItemClicked}>Delete</MenuItem>
          </Menu>
        </Row>
        <Typography
          variant="body1"
          color="text.secondary"
          fontSize="12px"
          lineHeight={1}
        >
          {formatDate(offer.created)}
        </Typography>
      </Column>
      <Row
        sx={{
          justifyContent: { xs: "space-between", sm: "space-around" },
          alignItems: "center",
          gap: 1,
        }}
      >
        <IconTitleValue
          Icon={TrendingUpOutlinedIcon}
          title="Signing Bonus"
          value={formatCurrency(offer.template.signing_bonus)}
          titleFontSize={titleFontSize}
        />
        <Divider orientation="vertical" sx={{ minHeight: "45px" }} />
        <IconTitleValue
          Icon={BalanceOutlinedIcon}
          title="Commission Ratio"
          value={commissionRatioDisplay(offer.template.commission_tiers)}
          titleFontSize={titleFontSize}
        />
      </Row>
      <OfferDetailsDialog
        open={offerDetailsDialogOpen}
        onClose={handleOfferDetailsDialogClosed}
        offer={offer}
      />
    </OfferPaper>
  )
}

function OfferSkeleton() {
  return (
    <OfferPaper>
      <Column>
        <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Skeleton variant="text" width={180} height={28} />
          <Row sx={{ alignItems: "center", gap: 1 }}>
            <Skeleton variant="rounded" width={64} height={22} />
            <Skeleton variant="circular" width={28} height={28} />
          </Row>
        </Row>
        <Skeleton variant="text" width={120} height={20} />
      </Column>
      <Row
        sx={{
          justifyContent: { xs: "space-between", sm: "space-around" },
          alignItems: "center",
          gap: 1,
        }}
      >
        <Row sx={{ alignItems: "center", gap: 1 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Column>
            <Skeleton variant="text" width={100} height={16} />
            <Skeleton variant="text" width={80} height={20} />
          </Column>
        </Row>
        <Divider orientation="vertical" sx={{ minHeight: "45px" }} />
        <Row sx={{ alignItems: "center", gap: 1 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Column>
            <Skeleton variant="text" width={120} height={16} />
            <Skeleton variant="text" width={100} height={20} />
          </Column>
        </Row>
      </Row>
    </OfferPaper>
  )
}

const OffersCol = styled(ColumnPaper)(() => ({
  height: "100%",
  backgroundImage: `url(${drawerBg})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundPosition: "center",
  "&:before": {
    display: "none",
  },
}))

const OfferPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.colors.gray50,
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}))

const OfferStatusChip = styled(BorderedGrayChip)(({ theme }) => ({
  fontSize: "12px",
  height: "22px",
  "& .MuiChip-label": { paddingInline: theme.spacing(1) },
}))
